import { z } from 'zod'
import { ProductionRoute, productionRouteAssetSchema } from '@/features/production-routes/api/ProductionRouteModel'
import { Unit } from '@/shared/config/config'
import { Currency } from '@/shared/hooks/api/appConfig'

export enum EmissionReportStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED'
}

const artifactSchema = z.any()
export type Artifacts = z.infer<typeof artifactSchema>

export const assetEmissionRecordSchema = z.object({
  id: z.string(),
  asset: productionRouteAssetSchema,
  emissionType: z.string(),
  method: z.string(),
  emission: z.number(),
  artifacts: artifactSchema
})

export type AssetEmissionRecord = z.infer<typeof assetEmissionRecordSchema>

const wasteGasEmissionParamsSchema = z.object({
  volume: z.string(),
  netCalorificValue: z.string(),
  emissionFactor: z.string(),
  correctionFactor: z.string().optional()
})

export type WasteGasParams = z.infer<typeof wasteGasEmissionParamsSchema>

export const wasteGasImportedEmissionRawSchema = z.object({
  emission: z.number(),
  artifacts: wasteGasEmissionParamsSchema
})

export const wasteGasExportedEmissionRawSchema = z.object({
  emission: z.number(),
  artifacts: wasteGasEmissionParamsSchema
})

export const heatEmissionRawSchema = z.object({
  heatImportedTj: z.string().min(1).nullable(),
  heatImportedEmissionFactor: z.string().min(1).nullable(),
  heatExportedTj: z.string().min(1).nullable(),
  heatExportedEmissionFactor: z.string().min(1).nullable(),
  heatImportedEmission: z.number().nullable(),
  heatExportedEmission: z.number().nullable()
})

export type HeatEmissionRaw = z.infer<typeof heatEmissionRawSchema>

export const carbonTaxPaidRawSchema = z.object({
  currency: z.enum(Currency).nullable(),
  effectiveCarbonPriceDue: z.string(),
  carbonPriceMechanismId: z.number().nullable(),
  exchangeRate: z.string(),
  embeddedEmissionsCovered: z.string(),
  embeddedEmissionsCoveredByRebate: z.string()
})

export type CarbonTaxPaidRaw = z.infer<typeof carbonTaxPaidRawSchema>

const endProductEmittingMethodInputSchema = z.object({
  id: z.number(),
  materialName: z.string(),
  deletedAt: z.date().optional().nullable(),
  proofFilePath: z.string(),
  quantityInTonnes: z.number(),
  type: z.enum(['OTHER_MATERIAL']),
  internalId: z.string().optional().nullable()
})

export type EndProductEmittingMethodInput = z.infer<typeof endProductEmittingMethodInputSchema>

const storedCO2AmountTonnesSchema = z.number()

export type StoredCO2AmountTonnes = z.infer<typeof storedCO2AmountTonnesSchema>

const massBalanceItemSchema = z.object({
  materialName: z.string(),
  description: z.string().optional(),
  quantity: z.number(),
  carbonContent: z.number(),
  emission: z.number()
})

export type MassBalanceItem = z.infer<typeof massBalanceItemSchema>

const fuelItemSchema = z.object({
  fuelType: z.string(),
  description: z.string().optional(),
  quantity: z.number(),
  emission: z.number(),
  artifacts: artifactSchema
})

const materialOutputItemSchema = z.object({
  carbonContent: z.object({
    quantity: z.number(),
    carbonContent: z.number(),
    emission: z.number()
  }),
  cnCode: z.string()
})

const materialInputItemSchema = materialOutputItemSchema

export type MaterialOutputItem = z.infer<typeof materialOutputItemSchema>
export type MaterialInputItem = z.infer<typeof materialInputItemSchema>
export type FuelItem = z.infer<typeof fuelItemSchema>

const electricityConsumptionSchema = z.object({
  consumptionTj: z.number(),
  consumptionMwh: z.number(),
  unit: z.enum([Unit.TJ, Unit.MWh]).default(Unit.TJ)
})

const activityLevelValidationSchema = z.object({
  hasToBeReEvaluated: z.boolean(),
  changeInStock: z.number(),
  conversions: z.number(),
  waste: z.number(),
  conversionsPercentage: z.number()
})

export type ActivityLevelValidation = z.infer<typeof activityLevelValidationSchema>

const methodEmissionReportPatchSchema = z.object({
  activityLevel: z.number().optional().nullable(),
  activityLevelValidation: activityLevelValidationSchema.optional(),
  emissionsRaw: z.array(assetEmissionRecordSchema).optional(),
  heatEmissionRaw: heatEmissionRawSchema.optional(),
  wasteGasImportedEmissionRaw: wasteGasImportedEmissionRawSchema.optional(),
  wasteGasExportedEmissionRaw: wasteGasExportedEmissionRawSchema.optional(),
  electricity: electricityConsumptionSchema.optional(),
  materialOutput: z.array(materialOutputItemSchema).optional(),
  otherMaterialsInput: z.array(massBalanceItemSchema).optional(),
  otherMaterialsOutput: z.array(massBalanceItemSchema).optional(),
  fuelInput: z.array(fuelItemSchema).optional(),
  scrapInput: z.array(massBalanceItemSchema).optional(),
  scrapOutput: z.array(massBalanceItemSchema).optional(),
  materialInput: z.array(materialInputItemSchema).optional(),
  storedCO2AmountTonnes: storedCO2AmountTonnesSchema.optional()
})

export type MethodEmissionReportPatch = z.infer<typeof methodEmissionReportPatchSchema>

export const methodEmissionReportSchema = z.object({
  ...methodEmissionReportPatchSchema.shape,
  id: z.string(),
  productionMethodCode: z.string(),
  cnCodes: z.array(z.string()),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  specificDirectEmbeddedEmissions: z.number(),
  specificIndirectEmbeddedEmissions: z.number(),
  totalIndirectEmissions: z.number(),
  totalDirectEmissions: z.number()
})

export type MethodEmissionReport = z.infer<typeof methodEmissionReportSchema>

export type WasteGasImportedEmission = z.infer<typeof wasteGasImportedEmissionRawSchema>

export type WasteGasExportedEmission = z.infer<typeof wasteGasExportedEmissionRawSchema>

const batchNumberSchema = z.object({
  batchNumber: z.string()
})

export type BatchNumber = z.infer<typeof batchNumberSchema>

export const batchNumbersUploadSchema = z.object({
  batchNumbers: z.array(batchNumberSchema)
})

export const electricityProductionMethodConfigSchema = z.object({
  consumption: z.number().gte(0),
  consumptionUnit: z.enum([Unit.TJ, Unit.MWh]),
  emissionFactor: z.number().gte(0),
  isEmissionFactorDefault: z.boolean(),
  fuelType: z.string().optional()
})

export const electricityRenewableProductionMethodConfigSchema = z.object({
  consumption: z.number().gte(0),
  consumptionUnit: z.enum([Unit.TJ, Unit.MWh]),
  emissionFactor: z.number(),
  isEmissionFactorDefault: z.boolean(),
  fuelType: z.string().optional()
})

export type ElectricityProductionMethodConfig = z.infer<
  typeof electricityProductionMethodConfigSchema
>

const electricityProductionConfigSchema = z.record(
  z.string(),
  electricityProductionMethodConfigSchema
)

export const emissionReportPatchPayloadSchema = z.object({
  electricityProductionConfig: electricityProductionConfigSchema.optional(),
  carbonTaxPaidRaw: carbonTaxPaidRawSchema.nullable().optional(),
  endProductEmittingMethodInputs: z.array(endProductEmittingMethodInputSchema).optional()
})

export type EmissionReportPatchPayload = z.infer<typeof emissionReportPatchPayloadSchema>

export const emissionReportSchema = z.object({
  id: z.string(),
  status: z.enum(EmissionReportStatus),
  submittedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  installationId: z.string(),
  productionRouteId: z.string(),
  startDate: z.iso.date(),
  endDate: z.iso.date(),
  name: z.string(),
  ...emissionReportPatchPayloadSchema.shape,
  electricityProductionConfig: electricityProductionConfigSchema
})

export type EmissionReport = z.infer<typeof emissionReportSchema>

export type EmissionReportWithProductionRoute = EmissionReport & {
  productionRoute: ProductionRoute
}
